"use client";

import { motion, useTransform } from "motion/react";
import { memo, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { axisLabelMaxWidth } from "../axis-label-budget";
import { useChartAxisSlots } from "../chart-axis-frame";
import { useChartProjection } from "../projection/chart-projection-context";
import {
  type ProjectedAxisLabel,
  ProjectedAxisLabels,
} from "../projection/projected-axis-labels";
import { useProjectionProgress } from "../projection/use-projected-path";
import { useHeatmap, useHeatmapInteractionOptional } from "./heatmap-context";
import { HEATMAP_AXIS_FADE } from "./heatmap-projection";
import {
  formatHeatmapYAxisLabel,
  getHeatmapDayLabels,
  type HeatmapYAxisLabelFormat,
  type HeatmapYAxisTickFilter,
  resolveHeatmapRowOpacity,
  shouldShowHeatmapYAxisTick,
} from "./heatmap-utils";

const CATEGORY_LABEL_CLASS =
  "text-chart-label text-xs leading-tight [overflow-wrap:anywhere]";
const DAY_LABEL_CLASS = "whitespace-nowrap text-chart-label text-xs";

export interface HeatmapYAxisProps {
  /** Additional class name for labels */
  className?: string;
  /** Which row ticks to display. Default: `"odd"` (Mon / Wed / Fri). */
  tickFilter?: HeatmapYAxisTickFilter;
  /** Label format — `"initial"` shows the first letter only (Mon → M). Default: `"full"`. */
  labelFormat?: HeatmapYAxisLabelFormat;
  /** Per-row label opacity — mirrors {@link HeatmapCells} `rowOpacity`. */
  rowOpacity?: number | readonly number[];
  /**
   * Row names, one per grid row. Replaces the weekday names for categorical
   * grids and labels every row instead of applying `tickFilter`.
   */
  labels?: readonly string[];
  /** Emphasises the label of the hovered cell's row. Default: false */
  highlightHovered?: boolean;
}

export function HeatmapYAxis(props: HeatmapYAxisProps) {
  const { containerRef } = useHeatmap();
  const slots = useChartAxisSlots();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const target = slots?.yRail ?? containerRef.current;
  if (!(mounted && target)) {
    return null;
  }

  return (
    <HeatmapYAxisInner
      {...props}
      framed={slots?.yRail != null}
      target={target}
    />
  );
}

const HeatmapYAxisInner = memo(function HeatmapYAxisInner({
  className,
  tickFilter = "odd",
  labelFormat = "full",
  rowOpacity,
  labels,
  highlightHovered = false,
  framed,
  target,
}: HeatmapYAxisProps & { framed: boolean; target: HTMLElement }) {
  const { data, margin, binHeight, gap, innerWidth, yScale, weekStartDay } =
    useHeatmap();
  const interaction = useHeatmapInteractionOptional();
  const { intlLocale } = useViewerLanguage();
  const hoveredRow = highlightHovered
    ? (interaction?.hoveredCell?.row ?? null)
    : null;
  const tickClass = (row: number) =>
    hoveredRow === row ? "font-medium text-foreground" : undefined;
  const rowCount = data[0]?.bins.length ?? 0;
  const isCategorical = labels != null;
  const projection = useChartProjection();
  const progress = useProjectionProgress(projection);
  const fadeOpacity = useTransform(progress, HEATMAP_AXIS_FADE, [1, 0]);
  const fade = projection ? fadeOpacity : undefined;

  const ticks = useMemo(() => {
    if (labels) {
      return labels
        .slice(0, rowCount)
        .map((label, row) => ({
          row,
          label,
          y: margin.top + yScale(row) + gap + (binHeight - gap) / 2,
        }));
    }

    const days = getHeatmapDayLabels(weekStartDay, intlLocale);
    const source = (rowCount > 0 ? days.slice(0, rowCount) : days).map(
      (label) => formatHeatmapYAxisLabel(label, labelFormat)
    );

    return source
      .map((label, row) => ({
        row,
        label,
        y: margin.top + yScale(row) + gap + (binHeight - gap) / 2,
      }))
      .filter((tick) => shouldShowHeatmapYAxisTick(tick.row, tickFilter));
  }, [
    binHeight,
    gap,
    labelFormat,
    labels,
    margin.top,
    rowCount,
    tickFilter,
    weekStartDay,
    yScale,
    intlLocale,
  ]);

  const projectedLabels = useMemo<ProjectedAxisLabel[]>(
    () =>
      projection
        ? ticks.map((tick) => ({
            key: String(tick.row),
            text: tick.label,
            x: innerWidth,
            y: yScale(tick.row) + gap + (binHeight - gap) / 2,
            z: 0,
          }))
        : [],
    [binHeight, gap, innerWidth, projection, ticks, yScale]
  );

  const projected = projection ? (
    <ProjectedAxisLabels
      axis="y"
      dx={8}
      dy={4}
      labels={projectedLabels}
      textAnchor="start"
    />
  ) : null;

  const labelClass = cn(
    isCategorical ? CATEGORY_LABEL_CLASS : DAY_LABEL_CLASS,
    className
  );
  const maxWidth = framed
    ? axisLabelMaxWidth(isCategorical ? "yAxisCategory" : "yAxisTick")
    : undefined;

  if (framed) {
    return (
      <>
        {projected}
        {createPortal(
          <div className="pointer-events-none relative">
            <div
              aria-hidden="true"
              className="invisible h-0 overflow-hidden pr-2 text-right"
            >
              {ticks.map((tick) => (
                <div className={labelClass} key={tick.row} style={{ maxWidth }}>
                  {tick.label}
                </div>
              ))}
            </div>
            {ticks.map((tick) => (
              <motion.div
                className="absolute right-0 flex items-center justify-end pr-2 text-right"
                key={tick.row}
                style={{ top: tick.y, transform: "translateY(-50%)", opacity: fade }}
              >
                <span
                  className={cn(labelClass, tickClass(tick.row))}
                  style={{
                    maxWidth,
                    opacity: resolveHeatmapRowOpacity(tick.row, rowOpacity),
                  }}
                >
                  {tick.label}
                </span>
              </motion.div>
            ))}
          </div>,
          target
        )}
      </>
    );
  }

  return (
    <>
      {projected}
      {createPortal(
        ticks.map((tick) => (
          <motion.div
            className="pointer-events-none absolute flex items-center justify-end"
            key={tick.row}
            style={{
              top: tick.y,
              left: 4,
              width: Math.max(margin.left - 12, 0),
              transform: "translateY(-50%)",
              opacity: fade,
            }}
          >
            <span
              className={cn(labelClass, tickClass(tick.row))}
              style={{ opacity: resolveHeatmapRowOpacity(tick.row, rowOpacity) }}
            >
              {tick.label}
            </span>
          </motion.div>
        )),
        target
      )}
    </>
  );
});

HeatmapYAxis.displayName = "HeatmapYAxis";

export default HeatmapYAxis;
